import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ExpenseData {
  description: string;
  amount: number;
}

interface ExpensePieChartProps {
  data: ExpenseData[];
  accountType: 'general' | 'reserve' | 'both';
  onDescriptionClick?: (description: string) => void;
}

export const ExpensePieChart: React.FC<ExpensePieChartProps> = ({
  data,
  accountType,
  onDescriptionClick,
}) => {
  const chartData = {
    labels: data.map(item => item.description),
    datasets: [
      {
        data: data.map(item => item.amount),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#C9CBCF',
          '#4BC0C0',
          '#FF9F40',
        ],
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 10,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'pie'>) => {
            const value = context.raw as number;
            const total = context.dataset.data.reduce((acc, curr) => acc + (curr as number), 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ¥${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onDescriptionClick) {
        const index = elements[0].index;
        const description = data[index].description;
        onDescriptionClick(description);
      }
    },
  };

  const getTitle = () => {
    switch (accountType) {
      case 'general':
        return '摘要別支出（一般会計）';
      case 'reserve':
        return '摘要別支出（積立会計）';
      case 'both':
        return '摘要別支出（全会計）';
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {getTitle()}
        </Typography>
        <Box sx={{ height: 300, position: 'relative' }}>
          <Pie data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};